import { usePrefetchQuery, useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { PATH_URL } from '@/constants/path-constants';
import { SECOND_MS } from '@/constants/time';
import { useExperimentEnabled } from '@/experiment/experiments-client';
import { subscriptionQuery } from '@/query/subscriptionQueryCreator';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import {
  shouldHideBillingPage,
  shouldHidePlansPage,
} from '@/shared/utils/subscriptionUtils';
import { useIsPersonalTemplatesEnabled } from '../hooks/useIsPersonalTemplatesEnabled';
import {
  getCachedTabEligibility,
  setCachedTabEligibility,
  type TabEligibility,
} from '../utils/tabEligibilityCache';

type NavItem = Readonly<{
  text: string;
  href: string;
  isActive?: (pathname: string) => boolean;
  enabled: boolean;
  dataTestId: string;
}>;

const FALLBACK_TIMEOUT_MS = 5 * SECOND_MS;

/**
 * Hook to get the navigation tabs shown on the settings pages.
 * Developer tab is only shown when 'public_api' experiment is enabled.
 * Returns items and an isReady flag that becomes true when tab eligibility data loads from cache/requests or after 5 seconds.
 */
export function useAccountSettingsNavItems(): {
  items: ReadonlyArray<NavItem>;
  isReady: boolean;
} {
  const t = useTranslations('settings.accountSettingsNavigation');
  const isPublicApiEnabled = useExperimentEnabled('public_api');
  const { data: subscription } = useQuery(subscriptionQuery());
  const {
    workspace,
    adminStatus,
    isLoading: isWorkspaceLoading,
  } = useWorkspace();

  const [eligibility, setEligibility] = useState<TabEligibility>({
    shouldHideBilling: false,
    shouldHidePlans: false,
    shouldHideNotifications: false,
  });

  const [hasCachedData, setHasCachedData] = useState(false);
  const [timeoutExpired, setTimeoutExpired] = useState(false);

  useEffect(() => {
    const cached = getCachedTabEligibility();
    if (cached) {
      setEligibility(cached);
      setHasCachedData(true);
    } else {
      // No cache exists, start fallback timeout
      const timer = setTimeout(() => {
        setTimeoutExpired(true);
      }, FALLBACK_TIMEOUT_MS);

      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!isWorkspaceLoading) {
      setEligibility((current) => ({
        ...current,
        shouldHideNotifications: Boolean(
          workspace?.settings?.disable_notifications_tab,
        ),
      }));
    }
  }, [workspace, isWorkspaceLoading]);

  useEffect(() => {
    if (subscription && !isWorkspaceLoading) {
      const newEligibility = {
        shouldHideBilling: shouldHideBillingPage(
          subscription,
          workspace,
          Boolean(adminStatus?.isAdmin),
        ),
        shouldHidePlans: shouldHidePlansPage(subscription, workspace),
        shouldHideNotifications: Boolean(
          workspace?.settings?.disable_notifications_tab,
        ),
      };

      setEligibility(newEligibility);
      setCachedTabEligibility(newEligibility);
      setHasCachedData(true);
    }
  }, [subscription, workspace, adminStatus?.isAdmin, isWorkspaceLoading]);

  const isReady = hasCachedData || timeoutExpired;

  const shouldHideBilling = eligibility.shouldHideBilling;
  const shouldHidePlans = eligibility.shouldHidePlans;
  const shouldHideNotifications = eligibility.shouldHideNotifications;

  const { isPersonalTemplatesEnabled } = useIsPersonalTemplatesEnabled();

  const baseItems: NavItem[] = [
    {
      text: t('general'),
      href: PATH_URL.SETTINGS_GENERAL,
      enabled: true,
      dataTestId: 'account-settings-nav-general',
    },
    {
      text: t('meetings'),
      href: PATH_URL.SETTINGS_MEETINGS,
      enabled: true,
      dataTestId: 'account-settings-nav-meetings',
    },

    {
      text: t('plans'),
      href: PATH_URL.SETTINGS_PLANS,
      enabled: !shouldHidePlans,
      dataTestId: 'account-settings-nav-plans',
    },
    {
      text: t('billing'),
      href: PATH_URL.SETTINGS_PLAN,
      enabled: !shouldHideBilling,
      dataTestId: 'account-settings-nav-billing',
    },
    {
      text: t('integrations'),
      href: PATH_URL.SETTINGS_INTEGRATIONS,
      enabled: true,
      isActive: (pathname) =>
        pathname.startsWith(PATH_URL.SETTINGS_INTEGRATIONS),
      dataTestId: 'account-settings-nav-apps-and-integrations',
    },
    {
      text: t('notifications'),
      href: PATH_URL.SETTINGS_NOTIFICATIONS,
      enabled: !shouldHideNotifications,
      dataTestId: 'account-settings-nav-notifications',
    },
    {
      text: t('templates'),
      href: PATH_URL.SETTINGS_PERSONAL_TEMPLATES,
      isActive: (pathname) =>
        pathname.startsWith(PATH_URL.SETTINGS_PERSONAL_TEMPLATES),
      enabled: isPersonalTemplatesEnabled,
      dataTestId: 'account-settings-nav-templates',
    },
    {
      text: t('security'),
      href: PATH_URL.SETTINGS_SECURITY,
      enabled: true,
      dataTestId: 'account-settings-nav-security',
    },
    {
      text: t('developer'),
      href: PATH_URL.SETTINGS_DEVELOPER,
      enabled: isPublicApiEnabled, // Only add Developer tab if experiment is enabled
      dataTestId: 'account-settings-nav-developer',
    },
  ];

  const items = baseItems.filter((item) => item.enabled);

  return {
    items,
    isReady,
  };
}

export function usePrefetchAccountSettingsNavItems() {
  usePrefetchQuery(subscriptionQuery());
}
